import type { FlagsType } from '@iheartjane/feature-flags';
import {
  featureFlagProperties,
  normalizeMixpanelPropertyValues,
} from '@iheartjane/tracking';
import mixpanelBrowser from 'mixpanel-browser';

import type { BloomMenuStore } from '@jane/bloom/types/schemas/roots/bloom/menu';

import type { MixpanelEvent } from './Events';

let initialized = false;

const ANALYTICS_TAGS = ['utm_source', 'utm_medium', 'utm_campaign'];

/**
 * Initialize Mixpanel SDK. Should be called as early as possible in entry.client.tsx.
 * This only does the core init - call registerMixpanelIdentity() after hydration
 * to set user identity and super properties.
 */
export function initMixpanel(projectToken: string, debug = false) {
  if (initialized || typeof window === 'undefined') {
    return;
  }

  mixpanelBrowser.init(projectToken, {
    debug,
    ignore_dnt: debug,
    persistence: 'localStorage',
  });
  initialized = true;
}

type MixpanelIdentityConfig = {
  buildVersion: string;
  featureFlags?: FlagsType;
  operatorId: number;
  operatorName: string;
  pluginVersion?: string;
  storeCity: string;
  storeId: number;
  storeName: string;
  storeState: string;
  uniqueId: string;
};

/**
 * Register user identity and super properties with Mixpanel.
 * Should be called in a useEffect after the app has hydrated and loader data is available.
 */
export function registerMixpanelIdentity({
  buildVersion,
  featureFlags,
  operatorId,
  operatorName,
  pluginVersion,
  storeCity,
  storeId,
  storeName,
  storeState,
  uniqueId,
}: MixpanelIdentityConfig) {
  if (!initialized) {
    return;
  }

  mixpanelBrowser.identify(uniqueId);
  mixpanelBrowser.register({
    app: 'bloom',
    build: buildVersion.substring(0, 7),
    featureFlags: featureFlagProperties(featureFlags),
    operatorId,
    'Operator Name': operatorName.toLowerCase(),
    pluginVersion,
    storeCity: storeCity.toLowerCase(),
    storeId,
    storeName: storeName.toLowerCase(),
    storeState: storeState.toLowerCase(),
  });
}

export function track({ event, ...rest }: MixpanelEvent) {
  if (!initialized) {
    return;
  }

  try {
    const normalizedProperties = normalizeMixpanelPropertyValues(rest);
    mixpanelBrowser.track(event, normalizedProperties);
  } catch (e) {
    console.warn('Mixpanel tracking error: ', e);
  }
}

export function registerJdid(jdid: string) {
  if (initialized && jdid) {
    mixpanelBrowser.register({ janeDeviceId: jdid });
  }
}

/**
 * Register store-related super properties. Used by store switcher hooks
 * when the pinned store changes after initial identity registration.
 */
export function registerSuperPropertiesStore(store?: BloomMenuStore | null) {
  mixpanelBrowser.register({
    storeCity: store?.city.toLowerCase(),
    storeId: store?.id,
    storeName: store?.name.toLowerCase(),
    storeState: store?.state.toLowerCase(),
  });
}

export function getMixpanelAnalyticsTags(): string | null {
  if (!initialized) {
    return null;
  }

  const params = new URLSearchParams();

  for (const tag of ANALYTICS_TAGS) {
    const value = mixpanelBrowser.get_property(tag);
    if (value) {
      params.append(tag, value);
    }
  }

  return params.toString() || null;
}
